import React, { useCallback, useEffect, useState } from "react";
import { Button } from "../common/Button";
import { useStepData } from "../../hooks/useStepData";
import { useFunnelNavigation } from "../../hooks/useFunnelNavigation";
import { InfoBox } from "../common/InfoBox";
import { InputField } from "../common/InputField";
import { useFunnel } from "../../context/FunnelContext";
import { StepHeader } from "../common/StepHeader";
import { MeasurementToggle } from "../common/MeasurementToggle";
import { useMeasurementSystem } from "../../hooks/useMeasurementSystem";

const WEIGHT_GOAL_LIMITS = {
  imperial: {
    lbs: { min: 80, max: 400 },
  },
  metric: {
    kg: { min: 36, max: 181 },
  },
};

export function WeightGoal({ config }) {
  const { measurementSystem } = useMeasurementSystem();
  const { data, updateStepData } = useStepData(config.field);
  const { state } = useFunnel();
  const { nextStep } = useFunnelNavigation();

  const [weightGoal, setWeightGoal] = useState(data?.weight || "");
  const [unit, setUnit] = useState(measurementSystem);
  const [weightLossPercentage, setWeightLossPercentage] = useState(null);

  const calculateWeightLossPercentage = useCallback(() => {
    const currentWeight = state.userData.weight?.weight;
    if (!currentWeight || !weightGoal) return null;

    let currentWeightKg =
      state.userData.weight.unit === "imperial"
        ? currentWeight * 0.453592
        : currentWeight;

    let goalWeightKg = unit === "imperial" ? weightGoal * 0.453592 : weightGoal;

    const weightLoss =
      ((currentWeightKg - goalWeightKg) / currentWeightKg) * 100;
    return Math.round(weightLoss);
  }, [weightGoal, unit, state.userData.weight]);

  useEffect(() => {
    const percentage = calculateWeightLossPercentage();
    setWeightLossPercentage(percentage);
  }, [calculateWeightLossPercentage]);

  const [error, setError] = useState("");

  const handleWeightChange = (value) => {
    setWeightGoal(value);

    // Validate weight and set error message
    const numWeight = Number(value);
    const limits = WEIGHT_GOAL_LIMITS[unit][unit === "imperial" ? "lbs" : "kg"];

    if (value && (numWeight < limits.min || numWeight > limits.max)) {
      if (numWeight < limits.min) {
        setError(
          `Please enter a weight of at least ${limits.min} ${
            unit === "imperial" ? "lbs" : "kg"
          }.`,
        );
      } else {
        setError(
          `Please enter a weight less than ${limits.max} ${
            unit === "imperial" ? "lbs" : "kg"
          }.`,
        );
      }
    } else {
      setError("");
    }
  };

  const isValidWeight = () => {
    const numWeight = Number(weightGoal);
    const limits = WEIGHT_GOAL_LIMITS[unit][unit === "imperial" ? "lbs" : "kg"];
    return numWeight >= limits.min && numWeight <= limits.max;
  };

  const getWeightLossMessage = () => {
    if (weightLossPercentage > 30) {
      return {
        title: "Challenging Goal",
        message: `You're aiming to lose ${weightLossPercentage}% of your weight. Consider setting a more moderate initial goal - you can always adjust it later.`,
        variant: "warning",
        badge: "High Risk", // Added badge text
      };
    }
    if (weightLossPercentage > 20) {
      return {
        title: "Ambitious Goal",
        message:
          "This is an ambitious goal. Remember that sustainable weight loss is typically slower but more effective in the long term.",
        variant: "warning",
        badge: "Ambitious", // Added badge text
      };
    }
    if (weightLossPercentage > 10) {
      return {
        title: "Healthy Goal",
        message:
          "There is scientific evidence that some obesity-related conditions can be improved with a 10% or greater weight loss.",
        variant: "success",
        badge: "Recommended", // Added badge text
      };
    }
    if (weightLossPercentage > 0) {
      return {
        title: "Modest Goal",
        message:
          "Even a modest weight loss of 5-10% can produce significant health benefits.",
        variant: "info",
        badge: "Moderate", // Added badge text
      };
    }
    if (weightLossPercentage < 0) {
      return {
        title: "Weight Gain Goal",
        message: "We'll help you gain weight in a healthy and sustainable way.",
        variant: "purple",
        badge: "Gain", // Added badge text
      };
    }
    return null;
  };

  const handleNext = () => {
    if (isValidWeight()) {
      updateStepData({ weight: weightGoal, unit });
      nextStep();
    }
  };

  const weightMessage = getWeightLossMessage();

  return (
    <div className="flex flex-col px-4">
      <div className="space-y-4">
        <StepHeader
          title={config.title}
          subtitle={config.subtitle}
          order={config.headerOrder}
        />

        <MeasurementToggle onChange={setUnit} />

        <div className="relative">
          <InputField
            value={weightGoal}
            onChange={handleWeightChange}
            placeholder={unit === "imperial" ? "150" : "68"}
            unit={unit === "imperial" ? "lbs" : "kg"}
            type="number"
            min={
              WEIGHT_GOAL_LIMITS[unit][unit === "imperial" ? "lbs" : "kg"].min
            }
            max={
              WEIGHT_GOAL_LIMITS[unit][unit === "imperial" ? "lbs" : "kg"].max
            }
            error={error} // Pass the error message to InputField
          />
        </div>

        {weightMessage && isValidWeight() && (
          <InfoBox
            variant={weightMessage.variant}
            title={weightMessage.title}
            text={weightMessage.message}
            badge={weightMessage.badge}
          />
        )}

        {weightGoal &&
          !error && ( // Add !error to the condition
            <Button
              onClick={handleNext}
              className="w-full"
              disabled={!isValidWeight()}
            >
              Next
            </Button>
          )}
      </div>
    </div>
  );
}
